/**
 * Phase 25A · System Setup — super-admin-only migration runner UI.
 *
 * Layout: two tabs (Pending / Applied), each row shows title + description +
 * category + dependency chips. Row-level buttons: "Preview" (dry-run modal),
 * "Apply" (guarded by unapplied deps). Top-right: "Apply All Pending".
 * Applied rows also show a "Roll back" button that opens the data-loss dialog.
 *
 * All state fetched via /api/system-setup/registry.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, CheckCircle2, PlayCircle, RefreshCw, Rewind, ShieldAlert, WandSparkles, ClipboardList, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";

const CATEGORY_STYLE = {
  migration:     "bg-sky-50 text-sky-700 border-sky-200",
  seed:          "bg-emerald-50 text-emerald-700 border-emerald-200",
  index:         "bg-violet-50 text-violet-700 border-violet-200",
  documentation: "bg-neutral-100 text-neutral-600 border-neutral-200",
  placeholder:   "bg-neutral-100 text-neutral-600 border-neutral-200",
};

const STATE_STYLE = {
  pending:     "bg-amber-50 text-amber-700 border-amber-200",
  applied:     "bg-emerald-50 text-emerald-700 border-emerald-200",
  rolled_back: "bg-rose-50 text-rose-700 border-rose-200",
};

function chip(text, styleKey, testid) {
  return (
    <span data-testid={testid}
      className={`inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium ${styleKey}`}>
      {text}
    </span>
  );
}

export default function SystemSetup() {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [applied, setApplied] = useState(0);
  const [pending, setPending] = useState(0);
  const [tab, setTab] = useState("pending");
  const [busyKey, setBusyKey] = useState(null);
  const [applyAllBusy, setApplyAllBusy] = useState(false);

  // Dry-run preview modal state
  const [previewKey, setPreviewKey] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Rollback confirmation state
  const [rollbackEntry, setRollbackEntry] = useState(null);

  // Phase 25B — SSE progress stream state
  const [streamOpen, setStreamOpen] = useState(false);
  const [streamEvents, setStreamEvents] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/system-setup/registry");
      setItems(data.items || []);
      setApplied(data.applied_count || 0);
      setPending(data.pending_count || 0);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const appliedMap = useMemo(() => {
    const m = {};
    for (const i of items) m[i.key] = i.state;
    return m;
  }, [items]);

  const isDepSatisfied = useCallback((entry) => {
    if (!entry.depends_on?.length) return true;
    return entry.depends_on.every((d) => {
      const s = appliedMap[d];
      // Consider "applied" or schema-detected as fulfilled (avoids blocking
      // a user who has out-of-band applied migrations).
      const detected = items.find((i) => i.key === d)?.detected_applied_by_schema;
      return s === "applied" || detected;
    });
  }, [appliedMap, items]);

  const pendingItems  = items.filter((i) => i.state !== "applied");
  const appliedItems  = items.filter((i) => i.state === "applied");

  const openPreview = async (entry) => {
    setPreviewKey(entry.key);
    setPreviewData(null);
    setPreviewLoading(true);
    try {
      const { data } = await api.post(`/system-setup/dry-run/${entry.key}`);
      setPreviewData({ entry, ...data });
    } catch (e) {
      toast.error(formatApiError(e));
      setPreviewKey(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const doApply = async (entry) => {
    setBusyKey(entry.key);
    try {
      const { data } = await api.post(`/system-setup/apply/${entry.key}`);
      if (data.status === "OK") {
        toast.success(`Applied ${entry.title}`);
      } else {
        toast.error(`Apply failed: ${data.raw_output?.slice(-500) || "unknown"}`);
      }
      setPreviewKey(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyKey(null);
    }
  };

  const doRollback = async (entry) => {
    setBusyKey(entry.key);
    try {
      const { data } = await api.post(`/system-setup/rollback/${entry.key}`,
        { acknowledge_data_loss: true });
      if (data.status === "OK") toast.success(`Rolled back ${entry.title}`);
      else toast.error(`Rollback failed: ${data.raw_output?.slice(-500) || "unknown"}`);
      setRollbackEntry(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyKey(null);
    }
  };

  const applyAll = async () => {
    setApplyAllBusy(true);
    setStreamEvents([]);
    setStreamOpen(true);
    try {
      // Phase 25B — SSE progress stream
      const { API_BASE, getToken } = await import("@/lib/api");
      const url = `${API_BASE}/system-setup/apply-all-pending/stream`;
      const resp = await fetch(url, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!resp.ok) throw new Error(`Stream failed: HTTP ${resp.status}`);
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const chunks = buf.split("\n\n");
        buf = chunks.pop() || "";
        for (const chunk of chunks) {
          const m = chunk.match(/^data: (.+)$/m);
          if (!m) continue;
          try {
            const evt = JSON.parse(m[1]);
            setStreamEvents((prev) => [...prev, evt]);
          } catch {}
        }
      }
      toast.success("Apply-all-pending stream complete.");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setApplyAllBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-4 sm:p-6" data-testid="system-setup-page">
      <PageHeader
        breadcrumbs={[{ label: "Admin", to: "/" }, { label: "System Setup" }]}
        title="System Setup"
        subtitle="Super Admin only. Apply pending migrations and seed data, and review what has already run on this environment."
        favorite={{ key: "admin.system-setup", type: "screen", label: "System Setup", route: "/admin/system-setup" }}
        testid="system-setup-page-header"
        actions={
          <Button variant="outline" size="sm" onClick={load} disabled={loading}
            data-testid="system-setup-refresh">
            <RefreshCw className={`mr-1.5 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        }
      />

      {/* Top banner */}
      <Card data-testid="system-setup-banner"
        className={pending > 0 ? "border-amber-300 bg-amber-50" : "border-emerald-300 bg-emerald-50"}>
        <CardContent className="flex items-center gap-3 p-4">
          {pending > 0
            ? <AlertTriangle className="h-6 w-6 text-amber-600" />
            : <CheckCircle2 className="h-6 w-6 text-emerald-600" />}
          <div className="flex-1">
            <div className="text-base font-medium" data-testid="system-setup-banner-text">
              {pending === 0
                ? "All migrations up to date."
                : `${pending} migration${pending === 1 ? "" : "s"} pending.`}
            </div>
            <div className="text-xs text-muted-foreground">
              {applied} applied · {pending} pending · {items.length - applied - pending} placeholder(s)
            </div>
          </div>
          <Button onClick={applyAll} disabled={pending === 0 || applyAllBusy}
            data-testid="system-setup-apply-all">
            {applyAllBusy
              ? <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> Applying…</>
              : <><WandSparkles className="mr-1.5 h-4 w-4" /> Apply All Pending</>}
          </Button>
        </CardContent>
      </Card>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="system-setup-tabs">
          <TabsTrigger value="pending" data-testid="system-setup-tab-pending">
            Pending ({pendingItems.length})
          </TabsTrigger>
          <TabsTrigger value="applied" data-testid="system-setup-tab-applied">
            Applied ({appliedItems.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="mt-4 space-y-3">
          {pendingItems.length === 0 ? (
            <Card><CardContent className="p-6 text-center text-sm text-muted-foreground">
              No pending migrations. Everything is up to date.
            </CardContent></Card>
          ) : pendingItems.map((entry) => (
            <MigrationRow key={entry.key} entry={entry}
              depOk={isDepSatisfied(entry)}
              busy={busyKey === entry.key}
              onPreview={() => openPreview(entry)}
              onApply={() => doApply(entry)} />
          ))}
        </TabsContent>

        <TabsContent value="applied" className="mt-4 space-y-3">
          {appliedItems.length === 0 ? (
            <Card><CardContent className="p-6 text-center text-sm text-muted-foreground">
              Nothing applied yet.
            </CardContent></Card>
          ) : appliedItems.map((entry) => (
            <MigrationRow key={entry.key} entry={entry}
              depOk={true}
              busy={busyKey === entry.key}
              onPreview={() => openPreview(entry)}
              onRollback={() => setRollbackEntry(entry)} />
          ))}
        </TabsContent>
      </Tabs>

      {/* Dry-run preview modal */}
      <Dialog open={!!previewKey} onOpenChange={(o) => !o && setPreviewKey(null)}>
        <DialogContent className="max-w-2xl" data-testid="system-setup-preview-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardList className="h-5 w-5 text-sky-600" />
              {previewData?.entry?.title || "Migration preview"}
            </DialogTitle>
            <DialogDescription>
              What this migration will do if you apply it. No changes have been made.
            </DialogDescription>
          </DialogHeader>
          {previewLoading ? (
            <div className="flex items-center justify-center p-8 text-muted-foreground">
              <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Running dry-run…
            </div>
          ) : previewData ? (
            <PreviewBody previewData={previewData} />
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewKey(null)}
              data-testid="system-setup-preview-close">Close</Button>
            {previewData && previewData.entry.state !== "applied" && !previewData.entry.placeholder && (
              <Button onClick={() => doApply(previewData.entry)}
                disabled={!isDepSatisfied(previewData.entry) || busyKey === previewData.entry.key}
                data-testid="system-setup-preview-apply">
                {busyKey === previewData.entry.key
                  ? <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> Applying…</>
                  : <><PlayCircle className="mr-1.5 h-4 w-4" /> Apply now</>}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rollback confirm */}
      <AlertDialog open={!!rollbackEntry} onOpenChange={(o) => !o && setRollbackEntry(null)}>
        <AlertDialogContent data-testid="system-setup-rollback-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-rose-600" />
              Roll back {rollbackEntry?.title}?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm">
                <div>The following will happen:</div>
                <ul className="list-disc space-y-1 pl-5 text-rose-700">
                  {(rollbackEntry?.data_loss_on_rollback || []).map((s, i) => (
                    <li key={i}>{s}</li>
                  ))}
                </ul>
                <div className="text-xs text-muted-foreground">
                  This action is auditable and can be re-applied later.
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="system-setup-rollback-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => rollbackEntry && doRollback(rollbackEntry)}
              disabled={busyKey === rollbackEntry?.key}
              data-testid="system-setup-rollback-confirm"
              className="bg-rose-600 hover:bg-rose-700">
              <Rewind className="mr-1.5 h-4 w-4" />
              Yes, roll back
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {/* Phase 25B — SSE progress panel for Apply-All-Pending */}
      <Dialog open={streamOpen} onOpenChange={(o) => { if (!applyAllBusy) setStreamOpen(o); }}>
        <DialogContent className="max-w-2xl" data-testid="system-setup-stream-panel">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <WandSparkles className="h-5 w-5 text-emerald-600" />
              Apply All Pending — live progress
            </DialogTitle>
            <DialogDescription>
              Real-time progress from the server. Each migration streams a
              started → applied or failed event.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[400px] overflow-y-auto space-y-2 rounded-md border p-3 bg-neutral-50">
            {streamEvents.length === 0 ? (
              <div className="flex items-center justify-center py-8 text-muted-foreground">
                <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Awaiting first event…
              </div>
            ) : streamEvents.map((evt, i) => (
              <div key={i} className="flex items-center gap-3 rounded-md bg-white border p-2 text-sm"
                data-testid={`stream-event-${i}`}>
                <span className={`inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-semibold ${
                  evt.status === "applied" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                  evt.status === "failed"  ? "bg-rose-50 text-rose-700 border-rose-200" :
                  evt.status === "skipped" ? "bg-neutral-100 text-neutral-600 border-neutral-200" :
                                             "bg-sky-50 text-sky-700 border-sky-200"
                }`}>
                  {evt.status}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-mono truncate">{evt.migration_key}</div>
                  <div className="text-[11px] text-muted-foreground truncate">{evt.summary_snippet}</div>
                </div>
                <div className="text-[11px] text-muted-foreground w-14 text-right">{evt.elapsed_ms || 0}ms</div>
                <div className="w-24 rounded-full bg-neutral-200 h-1.5 overflow-hidden">
                  <div className="h-full bg-emerald-500 transition-all"
                    style={{ width: `${Math.min(100, evt.progress_pct || 0)}%` }} />
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStreamOpen(false)} disabled={applyAllBusy}
              data-testid="system-setup-stream-close">
              {applyAllBusy ? "Streaming…" : "Close"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MigrationRow({ entry, depOk, busy, onPreview, onApply, onRollback }) {
  const stateLabel = entry.placeholder ? "coming soon" : entry.state.replace("_", " ");
  const stateStyle = entry.placeholder ? CATEGORY_STYLE.placeholder : STATE_STYLE[entry.state];
  return (
    <Card data-testid={`migration-row-${entry.key}`}>
      <CardContent className="p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0 flex-1 space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <div className="text-base font-medium" data-testid={`migration-title-${entry.key}`}>
                {entry.title}
              </div>
              {chip(stateLabel, stateStyle, `migration-state-${entry.key}`)}
              {chip(entry.category, CATEGORY_STYLE[entry.category] || CATEGORY_STYLE.migration,
                    `migration-cat-${entry.key}`)}
              {entry.placeholder && chip("placeholder", CATEGORY_STYLE.placeholder, `migration-placeholder-${entry.key}`)}
            </div>
            <div className="text-sm text-muted-foreground">{entry.description}</div>
            {entry.depends_on?.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5 text-xs">
                <span className="text-muted-foreground">Depends on:</span>
                {entry.depends_on.map((d) => (
                  <Badge key={d} variant="outline" className="font-mono text-[10px]"
                    data-testid={`migration-dep-${entry.key}-${d}`}>
                    {d}
                  </Badge>
                ))}
              </div>
            )}
            {entry.applied_row?.applied_at && (
              <div className="text-xs text-muted-foreground" data-testid={`migration-applied-at-${entry.key}`}>
                Applied {new Date(entry.applied_row.applied_at).toLocaleString()} · runtime {entry.applied_row.runtime_ms || 0}ms
              </div>
            )}
          </div>
          <div className="flex flex-shrink-0 flex-wrap items-center gap-2">
            {!entry.placeholder && (
              <Button size="sm" variant="outline" onClick={onPreview}
                data-testid={`migration-preview-btn-${entry.key}`}>
                <ClipboardList className="mr-1.5 h-4 w-4" />
                Preview
              </Button>
            )}
            {onApply && !entry.placeholder && (
              <Button size="sm" onClick={onApply} disabled={!depOk || busy}
                data-testid={`migration-apply-btn-${entry.key}`}>
                {busy
                  ? <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> Applying…</>
                  : <><PlayCircle className="mr-1.5 h-4 w-4" /> Apply</>}
              </Button>
            )}
            {onRollback && entry.down_available && !entry.placeholder && (
              <Button size="sm" variant="destructive" onClick={onRollback} disabled={busy}
                data-testid={`migration-rollback-btn-${entry.key}`}>
                <Rewind className="mr-1.5 h-4 w-4" />
                Roll back
              </Button>
            )}
          </div>
        </div>
        {!depOk && !entry.placeholder && entry.state !== "applied" && (
          <div className="mt-3 flex items-start gap-2 rounded-md bg-amber-50 p-2 text-xs text-amber-800"
            data-testid={`migration-dep-warn-${entry.key}`}>
            <AlertTriangle className="h-4 w-4 flex-shrink-0" />
            One or more upstream migrations are not yet applied. Apply them first.
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function PreviewBody({ previewData }) {
  const sentences = previewData.sentences || [];
  const warnings = previewData.warnings || [];
  return (
    <div className="space-y-4">
      <div className="rounded-md bg-neutral-50 p-3">
        <div className="mb-2 text-xs font-medium uppercase tracking-wide text-neutral-500">What will change</div>
        {sentences.length === 0 ? (
          <div className="text-sm text-muted-foreground">
            No changes would be made — this migration is already fully in effect.
          </div>
        ) : (
          <ul className="space-y-1.5 text-sm">
            {sentences.map((s, i) => (
              <li key={i} className="flex items-start gap-2"
                data-testid={`preview-sentence-${i}`}>
                <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-sky-600" />
                <span>{s}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
      {warnings.length > 0 && (
        <div className="rounded-md bg-amber-50 p-3">
          <div className="mb-2 text-xs font-medium uppercase tracking-wide text-amber-700">
            Heads up
          </div>
          <ul className="space-y-1 text-sm text-amber-800">
            {warnings.map((w, i) => (
              <li key={i} data-testid={`preview-warning-${i}`}>· {w}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
